/**
 * Direct Messages (paid) API Client
 *
 * API Endpoints:
 * - POST /dm/channels/{channelUuid}/messages → DmSendResult (paid send, client_id idempotency)
 * - GET  /dm/threads → DmThreadsPage (subscriber inbox)
 * - GET  /dm/threads/{threadUuid}/messages → DmMessagesPage (serves both sides, oldest-first)
 * - POST /dm/threads/{threadUuid}/mark-read → void (subscriber side)
 * - GET  /studio/dm/threads → DmStudioThreadsPage (creator inbox)
 * - POST /studio/dm/threads/{threadUuid}/messages → DmSentMessage (free creator reply)
 * - POST /studio/dm/threads/{threadUuid}/mark-read → void (creator side)
 *
 * Access contract: non-participants get 404 on thread endpoints — never 403.
 */

import { isAxiosError } from 'axios';
import type {
  DmMessagesPage,
  DmPagination,
  DmSendResult,
  DmSentMessage,
  DmStudioThreadsPage,
  DmThreadsPage,
} from '@/types';
import { apiClient } from './base-client';

export interface DmListParams extends Record<string, unknown> {
  page?: number;
  per_page?: number;
}

export type DmSendErrorField = 'balance' | 'channel' | 'client_id';

const SEND_ERROR_FIELDS: readonly DmSendErrorField[] = ['balance', 'channel', 'client_id'];

/**
 * Field key of a field-keyed 422 send failure: `balance` = insufficient coins,
 * `channel` = opted out / unpriced / self-DM, `client_id` = ledger/message
 * mismatch (BE treats that one as a bug signal — captureError it at the
 * surface). Null for anything that isn't a field-keyed 422.
 */
export function getDmErrorField(error: unknown): DmSendErrorField | null {
  if (!isAxiosError(error) || error.response?.status !== 422) return null;
  const body = error.response.data as { errors?: Record<string, unknown> } | undefined;
  const errors = body?.errors;
  if (!errors || typeof errors !== 'object') return null;
  return SEND_ERROR_FIELDS.find((field) => field in errors) ?? null;
}

const emptyPagination = (): DmPagination => ({
  current_page: 1,
  per_page: 0,
  total: 0,
  last_page: 1,
});

export const dmClient = {
  /**
   * Paid send. `client_id` comes from getOrCreateSendClientId — retries after
   * network errors MUST reuse the same value (BE dedupes on it).
   */
  sendMessage: async (
    channelUuid: string,
    payload: { body: string; client_id: string }
  ): Promise<DmSendResult> => {
    const res = await apiClient.post<{ success?: boolean; data?: DmSendResult }>(
      `/dm/channels/${channelUuid}/messages`,
      payload
    );
    if (!res?.data?.message) throw new Error('Malformed DM send response');
    return res.data;
  },

  listThreads: async (params?: DmListParams): Promise<DmThreadsPage> => {
    const res = await apiClient.get<{ success?: boolean; data?: Partial<DmThreadsPage> }>(
      '/dm/threads',
      { params: { ...params } }
    );
    return {
      threads: Array.isArray(res?.data?.threads) ? res.data.threads : [],
      pagination: res?.data?.pagination ?? emptyPagination(),
    };
  },

  getMessages: async (threadUuid: string, params?: DmListParams): Promise<DmMessagesPage> => {
    const res = await apiClient.get<{ success?: boolean; data?: Partial<DmMessagesPage> }>(
      `/dm/threads/${threadUuid}/messages`,
      { params: { ...params } }
    );
    return {
      thread_uuid: res?.data?.thread_uuid ?? threadUuid,
      messages: Array.isArray(res?.data?.messages) ? res.data.messages : [],
      pagination: res?.data?.pagination ?? emptyPagination(),
    };
  },

  markRead: async (threadUuid: string): Promise<void> => {
    await apiClient.post(`/dm/threads/${threadUuid}/mark-read`);
  },

  listStudioThreads: async (params?: DmListParams): Promise<DmStudioThreadsPage> => {
    const res = await apiClient.get<{ success?: boolean; data?: Partial<DmStudioThreadsPage> }>(
      '/studio/dm/threads',
      { params: { ...params } }
    );
    return {
      threads: Array.isArray(res?.data?.threads) ? res.data.threads : [],
      pagination: res?.data?.pagination ?? emptyPagination(),
    };
  },

  /** Free creator reply — no client_id contract on this endpoint; debounce at the surface. */
  sendStudioReply: async (threadUuid: string, body: string): Promise<DmSentMessage> => {
    const res = await apiClient.post<{ success?: boolean; data?: { message?: DmSentMessage } }>(
      `/studio/dm/threads/${threadUuid}/messages`,
      { body }
    );
    if (!res?.data?.message) throw new Error('Malformed studio reply response');
    return res.data.message;
  },

  markStudioRead: async (threadUuid: string): Promise<void> => {
    await apiClient.post(`/studio/dm/threads/${threadUuid}/mark-read`);
  },
};
